import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  Mutation,
  PresignedUploadFormField,
  Query,
  UpdateProfilePhotoError,
  UpdateProfilePhotoResultTypenames,
} from '@varo/graphql-schema-types';
import {
  CameraType,
  ImagePickerAsset,
  ImagePickerOptions,
  ImagePickerResult,
  launchCameraAsync,
  launchImageLibraryAsync,
  requestCameraPermissionsAsync,
  requestMediaLibraryPermissionsAsync,
} from 'expo-image-picker';
import {
  UploadMultipartImageError,
  UploadMultipartImageSuccess,
} from 'features/rdc/screens/ReviewDeposit/hooks/uploadMultipartImage';
import { useEffect, useRef, useState } from 'react';
import getPresignedUpload from 'services/graphql/queries/GetPresignedUpload';
import updateProfilePhoto from 'services/graphql/queries/UpdateProfilePhoto';
import { isWeb } from 'shared/constants/platform';
import useGetProfileInfo from 'shared/hooks/useGetProfileInfo/useGetProfileInfo';
import { logBreadcrumb, logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { useMutation, useQuery } from 'urql';

const useProfilePhoto = () => {
  const [presignedUploadResponse, executePresignedUpload] = useQuery<Query>({
    query: getPresignedUpload,
    requestPolicy: 'network-only',
    pause: true,
  });

  const [, executeUpdateProfilePhoto] =
    useMutation<Mutation>(updateProfilePhoto);

  const [isPhotoUploaded, setIsPhotoUploaded] = useState<boolean>(false);

  const {
    result,
    fetching: isLoading,
    refetch,
  } = useGetProfileInfo(isPhotoUploaded ? 'network-only' : 'cache-first');

  const [file, setFile] = useState<ImagePickerAsset>();

  const bottomSheetRef = useRef<BottomSheetModal | null>(null);

  const [errorScreen, setErrorScreen] = useState<
    boolean | UpdateProfilePhotoError
  >(false);
  const [isUploading, setUploading] = useState<boolean>(false);

  const initiateProfilePhotoUpload = (image: ImagePickerResult) => {
    try {
      if (image?.assets?.length) {
        const fileDetails = image.assets[0];
        setUploading(true);
        setFile(fileDetails);
        executePresignedUpload();
      }
    } catch {
      // TODO- add analytics event JT-121593
      setErrorScreen(true);
    }
  };

  const generateFormData = (
    presignedUploadFormData: PresignedUploadFormField[],
  ) => {
    const formData = new FormData();
    presignedUploadFormData.map((item) => {
      formData.append(item.name, item.value);
    });
    if (!isWeb) {
      formData.append('file', {
        uri: file?.uri,
        type: file?.mimeType,
        fileName: file?.fileName,
      } as unknown as Blob);
      return formData;
    }
    formData.append('file', file?.file as unknown as Blob);
    return formData;
  };

  const handleUploadResult = async (
    uploadResult: UploadMultipartImageSuccess | UploadMultipartImageError,
  ) => {
    if (uploadResult.success && presignedUploadResponse.data) {
      const presignedIdMapResult = await executeUpdateProfilePhoto({
        input: {
          presignedUploadId: presignedUploadResponse.data.presignedUpload[0].id,
        },
      });

      if (
        presignedIdMapResult.data?.updateProfilePhoto.__typename ===
        UpdateProfilePhotoResultTypenames.UpdateProfilePhotoSuccess
      ) {
        setIsPhotoUploaded(true);
        refetch();
      } else if (
        presignedIdMapResult.data?.updateProfilePhoto.__typename ===
        UpdateProfilePhotoResultTypenames.UpdateProfilePhotoError
      ) {
        // TODO- add analytics event JT-121593
        setErrorScreen(presignedIdMapResult.data?.updateProfilePhoto);
      } else {
        setErrorScreen(true);
      }
    } else {
      // TODO- add analytics event JT-121593
      setErrorScreen(true);
    }
  };

  const uploadProfilePic = async (
    url: string,
    body: FormData,
  ): Promise<UploadMultipartImageSuccess | UploadMultipartImageError> => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        cache: 'no-cache',
        body: body,
      });
      if (!response.ok) {
        logErrorWithContext('getting error updating profile photo', {
          featureArea: FeatureArea.PROFILE_PHOTO_UPDATE,
          responsibleTeam: ResponsibleTeam.FRAUD,
          severity: ErrorSeverity.LOW,
        });
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      return { success: true, response };
    } catch (error) {
      logBreadcrumb({
        level: 'error',
        message: 'AccountLookUp error: ' + error,
      });
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  };

  const uploadPhoto = async () => {
    try {
      const presignedUploadResult =
        presignedUploadResponse?.data?.presignedUpload?.[0];
      if (presignedUploadResult && file?.uri) {
        const formData = generateFormData(presignedUploadResult.form);
        const uploadResult = await uploadProfilePic(
          presignedUploadResult.uploadUrl,
          formData,
        );
        if (uploadResult.success) {
          await handleUploadResult(uploadResult);
        } else {
          // TODO- add analytics event JT-121593
          setErrorScreen(true);
        }
        return uploadResult;
      }
    } catch {
      // TODO- add analytics event JT-121593
      setErrorScreen(true);
    }
  };

  const handlePresentBottomSheet = () => {
    bottomSheetRef.current?.present();
  };

  const handleCloseBottomSheet = () => {
    bottomSheetRef.current?.close();
  };

  const pickImage = async (isCamera: boolean) => {
    try {
      // TODO: handle for web - JT-121864
      const options: ImagePickerOptions = {
        cameraType: CameraType.front,
        allowsEditing: true,
        selectionLimit: 1,
        mediaTypes: ['images'],
        allowsMultipleSelection: false,
        aspect: [1, 1],
        quality: 0.2,
      };
      let result: ImagePickerResult;
      if (isCamera) {
        await requestCameraPermissionsAsync();
        result = await launchCameraAsync(options);
      } else {
        await requestMediaLibraryPermissionsAsync();
        result = await launchImageLibraryAsync(options);
      }
      initiateProfilePhotoUpload(result);
    } catch {
      setErrorScreen(true);
    } finally {
      handleCloseBottomSheet();
    }
  };

  useEffect(() => {
    uploadPhoto()
      .catch(() => {
        setErrorScreen(true);
      })
      .finally(() => {
        setUploading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presignedUploadResponse.data]);

  const bottomSheet = {
    ref: bottomSheetRef,
    show: handlePresentBottomSheet,
  };

  return {
    result,
    fetching: isLoading || isUploading,
    errorScreen,
    refetch,
    setErrorScreen,
    initiateProfilePhotoUpload,
    pickImage,
    bottomSheet,
  };
};

export default useProfilePhoto;
